/**
 * Bundle 80 Task 4 - Admin Test Mode console.
 * URL: /admin/test-mode
 *
 * Banner via a global effect; here the page hosts the surrogate list,
 * time-warp controls, walkthrough runner, and the surrogate inbox.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, Zap } from 'lucide-react';

const TIMEWARP_KINDS = [
  'connection_followup_14d',
  'connection_followup_60d',
  'success_story_flag_now',
  'survey_auto_send_live',
];


const AdminTestModePage = () => {
  const [state, setState] = useState({ enabled: false });
  const [surrogates, setSurrogates] = useState([]);
  const [walkthroughs, setWalkthroughs] = useState([]);
  const [inboxSurrogate, setInboxSurrogate] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [twKind, setTwKind] = useState(TIMEWARP_KINDS[0]);
  const [twTarget, setTwTarget] = useState('');

  const load = useCallback(async () => {
    try {
      const s = await api.get('/admin/test-mode/state');
      setState(s.data || { enabled: false });
      const g = await api.get('/admin/test-mode/surrogates');
      setSurrogates(g.data?.surrogates || []);
      const w = await api.get('/admin/test-mode/walkthroughs');
      setWalkthroughs(w.data?.walkthroughs || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    try {
      const { data } = await api.post('/admin/test-mode/toggle', { enabled: !state.enabled });
      setState({ enabled: data.enabled });
      toast.success(`Test Mode ${data.enabled ? 'ON' : 'OFF'}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Toggle failed'); }
  };

  const impersonate = async (sid) => {
    try {
      const { data } = await api.post(`/admin/test-mode/impersonate/${sid}`);
      // Open a new tab with the surrogate token stashed.
      const url = new URL(window.location.origin);
      url.searchParams.set('test_impersonation_token', data.token);
      window.open(url.toString(), '_blank', 'noopener');
      toast.info(`Opened surrogate ${data.surrogate?.email} in a new tab.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Impersonate failed'); }
  };

  const showInbox = async (sid) => {
    try {
      const { data } = await api.get(`/admin/test-mode/inbox/${sid}`);
      setInbox(data.inbox || []); setInboxSurrogate(sid);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load inbox failed'); }
  };

  const timewarp = async () => {
    try {
      const { data } = await api.post('/admin/test-mode/timewarp', { kind: twKind, target_id: twTarget });
      toast.success(`Time-warp complete: ${JSON.stringify(data.result)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Time-warp failed'); }
  };

  const purge = async () => {
    if (!window.confirm('Purge every test row across every collection?')) return;
    try {
      const { data } = await api.post('/admin/test-mode/purge');
      toast.success(`Purged: ${JSON.stringify(data.deleted)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Purge failed'); }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5" data-testid="admin-test-mode-page">
      {state.enabled ? (
        <div className="rounded-md bg-yellow-100 border border-yellow-300 p-3 text-yellow-900 flex items-center gap-2" data-testid="test-mode-banner">
          <AlertTriangle className="h-4 w-4" />
          <span className="font-semibold uppercase tracking-widest text-xs">Test Mode active</span>
          <span className="text-xs">Actions in this session are isolated from real data + emails.</span>
        </div>
      ) : null}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-secondary-800">Bundle 80 * Test Mode</div>
          <h1 className="font-display text-3xl font-bold text-primary-900 mt-1">Workflow simulators</h1>
          <p className="text-neutral-600 text-sm mt-1">Impersonate a surrogate, time-warp jobs, and watch intercepted emails.</p>
        </div>
        <div className="flex gap-2">
          <Button variant={state.enabled ? 'default' : 'outline'} onClick={toggle} data-testid="test-mode-toggle-btn">
            {state.enabled ? 'Turn OFF Test Mode' : 'Turn ON Test Mode'}
          </Button>
          <Button variant="outline" onClick={load} data-testid="test-mode-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
          <Button variant="outline" onClick={purge} data-testid="test-mode-purge-btn">Purge test data</Button>
        </div>
      </div>

      <Card><CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Test surrogates</div>
        <div className="grid gap-2 sm:grid-cols-2">
          {surrogates.map((s) => (
            <div key={s.id} className="flex items-center justify-between border border-neutral-200 rounded-md p-2" data-testid={`surrogate-row-${s.id}`}>
              <div>
                <div className="text-primary-900 font-semibold text-sm">
                  {s.profile?.first_name} {s.profile?.last_name}
                </div>
                <div className="text-neutral-500 text-xs font-mono">{s.email}</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => impersonate(s.id)} disabled={!state.enabled} data-testid={`surrogate-impersonate-${s.id}`}>Impersonate</Button>
                <Button size="sm" variant="outline" onClick={() => showInbox(s.id)} data-testid={`surrogate-inbox-${s.id}`}>Inbox</Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Time-warp</div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={twKind} onChange={(e) => setTwKind(e.target.value)}
            className="border border-neutral-200 rounded-md px-2 py-1 text-sm" data-testid="timewarp-kind-select">
            {TIMEWARP_KINDS.map((k) => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
          </select>
          <Input value={twTarget} onChange={(e) => setTwTarget(e.target.value)} placeholder="Target id (connection or event)"
            className="max-w-md" data-testid="timewarp-target-input" />
          <Button size="sm" onClick={timewarp} disabled={!state.enabled} className="bg-primary-900 text-white hover:bg-primary-800" data-testid="timewarp-fire-btn">
            <Zap className="h-3.5 w-3.5 mr-1" /> Fire now
          </Button>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Walkthroughs</div>
        {walkthroughs.map((w) => (
          <div key={w.name} className="mb-2" data-testid={`walkthrough-${w.name}`}>
            <div className="text-sm font-semibold text-primary-900">{w.name.replace(/_/g, ' ')}</div>
            <ol className="text-xs text-neutral-600 list-decimal ml-4 mt-1 space-y-0.5">
              {w.steps.map((s) => <li key={s.n}>{s.label}</li>)}
            </ol>
          </div>
        ))}
      </CardContent></Card>

      {inboxSurrogate ? (
        <Card><CardContent className="p-4" data-testid="inbox-panel">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Inbox for surrogate {inboxSurrogate}</div>
          {inbox.length === 0 ? <div className="text-neutral-500 text-sm">Empty.</div> : null}
          {inbox.map((m) => (
            <div key={m.id} className="border border-neutral-200 rounded-md p-2 mb-2" data-testid={`inbox-mail-${m.id}`}>
              <div className="text-xs text-neutral-500">{m.at}</div>
              <div className="font-semibold text-primary-900">{m.subject}</div>
              <div className="text-xs text-neutral-700" dangerouslySetInnerHTML={{ __html: m.html }} />
            </div>
          ))}
        </CardContent></Card>
      ) : null}
    </div>
  );
};

export default AdminTestModePage;
